import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import PageShell from "@/components/premium/PageShell";

interface ChildRow {
  id: string;
  display_name: string;
  age_range: string | null;
  consent_status: string;
  consent_required: boolean;
  created_at: string;
}
interface RequestRow {
  id: string;
  child_id: string | null;
  request_type: string;
  status: string;
  requested_at: string;
  completed_at: string | null;
}

export default function ParentPrivacy() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState<ChildRow[]>([]);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  const load = async () => {
    if (!user) return;
    setLoading(true);
    const [c, r] = await Promise.all([
      supabase.from("child_profiles").select("id, display_name, age_range, consent_status, consent_required, created_at").eq("parent_user_id", user.id),
      supabase.from("child_data_requests").select("id, child_id, request_type, status, requested_at, completed_at").eq("parent_user_id", user.id).order("requested_at", { ascending: false }).limit(25),
    ]);
    setChildren((c.data ?? []) as ChildRow[]);
    setRequests((r.data ?? []) as RequestRow[]);
    setLoading(false);
  };

  useEffect(() => { void load(); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const callFn = async (fn: string, childId: string, action: string) => {
    setBusy(childId + fn);
    try {
      if (fn === "export-child-data") {
        const { data, error } = await supabase.functions.invoke(fn, { body: { child_id: childId } });
        if (error) throw error;
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `child-data-${childId}.json`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } else {
        const { error } = await supabase.functions.invoke(fn, { body: { child_id: childId } });
        if (error) throw error;
      }
      toast.success(`${action} complete`);
      await load();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(`${action} failed: ${msg}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <PageShell>
      <Helmet>
        <title>Child Privacy Controls | HeirWise</title>
        <meta name="description" content="Manage child data, consent, exports, and deletions for your HeirWise learners." />
      </Helmet>
      <h1 className="text-3xl font-heading font-bold mb-2">Child Privacy Controls</h1>
      <p className="text-muted-foreground mb-6">
        Manage what HeirWise stores about each eligible teen learner, export their data, or manage permission.
        These technical controls do not represent a legal compliance certification, and attorney review remains recommended.
      </p>

      <section className="space-y-4 mb-10">
        <h2 className="text-xl font-semibold">Your learners</h2>
        {loading && <p className="text-muted-foreground">Loading…</p>}
        {!loading && children.length === 0 && (
          <p className="text-muted-foreground">No child profiles yet.</p>
        )}
        {children.map((c) => (
          <div key={c.id} className="rounded-2xl border border-border bg-card/60 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-semibold">{c.display_name}</div>
                <div className="text-xs text-muted-foreground">
                  {c.age_range ?? "age unspecified"} · consent: <strong>{c.consent_status}</strong>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => callFn("export-child-data", c.id, "Export")}>
                  Export profile and activity
                </Button>
                <Button size="sm" disabled={!!busy || c.consent_status === "granted"} onClick={() => callFn("grant-child-consent", c.id, "Grant consent")}>
                  Grant consent
                </Button>
                <Button size="sm" variant="outline" disabled={!!busy || c.consent_status === "revoked"} onClick={() => callFn("revoke-child-consent", c.id, "Revoke consent")}>
                  Revoke consent
                </Button>
                <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => {
                  if (confirm(`Permanently delete all data for ${c.display_name}? This cannot be undone.`)) {
                    void callFn("delete-child-data", c.id, "Delete");
                  }
                }}>
                  Delete learner profile
                </Button>
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="space-y-4 mb-10">
        <h2 className="text-xl font-semibold">Data requests</h2>
        {requests.length === 0 && <p className="text-muted-foreground text-sm">No requests yet.</p>}
        {requests.map((r) => (
          <div key={r.id} className="text-sm rounded-lg border border-border bg-card/40 p-3 flex justify-between gap-3">
            <span className="capitalize">{r.request_type.replace(/_/g, " ")}</span>
            <span className="text-muted-foreground">{r.status} · {new Date(r.requested_at).toLocaleString()}</span>
          </div>
        ))}
      </section>

      <section className="text-sm text-muted-foreground space-y-2">
        <h2 className="text-xl font-semibold text-foreground">Policies</h2>
        <ul className="list-disc pl-5 space-y-1">
          <li><Link to="/children-privacy-notice" className="underline">Teen Privacy Notice</Link></li>
          <li><Link to="/parental-consent" className="underline">Parent Authorization</Link></li>
          <li><Link to="/data-deletion-request" className="underline">Data Deletion Request</Link></li>
          <li><Link to="/privacy-policy" className="underline">Privacy Policy</Link></li>
        </ul>
      </section>
    </PageShell>
  );
}